import { useAppSelector } from "hooks/use-app-select";
import { SignerState } from "./ParticipantScreenService";
import { selectScreen } from "redux/appSlice";
import { Stack, Box, Typography, Button, Paper, Slide } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import viewpaths from "global/constants/ViewPathConstants";
import strings from "global/constants/StringConstants";
import { postSignSignupSearch } from "helpers/utm";

const ParticipantsPrompts = (props: {
  state: SignerState;
  openSignerLoginPopup: boolean;
  setSignerLoginPopup: Function;
  allFieldsFilledPopup: boolean;
  setAllFeildsFilledPopup: Function;
  handleFinish: Function;
  loading: boolean;
}) => {
  const urlParams = window.location.search;
  const reqToken = new URLSearchParams(urlParams).get("id");
  const isMobile = useAppSelector(selectScreen);

  const handleSignUp = () => {
    window.open(
      viewpaths.registerViewPath +
        postSignSignupSearch(strings.UTM_CONTENT_SIGNUP_PROMPT),
      "_blank",
    );
  };

  const handleLogin = () => {
    window.open(viewpaths.loginViewPath, "_blank");
  };

  const handleCancel = () => {
    props.setAllFeildsFilledPopup(false);
  };

  const handleCloseSignerLogin = () => {
    props.setSignerLoginPopup(false);
  };

  const transitionDuration = 600; // slower animation in ms

  const renderSignupPrompt = () => (
    <Slide
      direction="up"
      in={props.openSignerLoginPopup}
      timeout={transitionDuration}
      mountOnEnter
      unmountOnExit
    >
      <Paper
        elevation={3}
        role="status"
        aria-live="polite"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "white",
          border: "1px solid #e0e0e0",
        }}
      >
        <Stack
          direction={isMobile ? "column" : "row"}
          p={2.5}
          justifyContent="center"
          alignItems="center"
        >
          <Box textAlign="center">
            <Typography gutterBottom variant="body1" fontWeight="bold">
              Document signed successfully!
            </Typography>
            <Typography gutterBottom variant="subtitle2">
              Your document will be sent to your email.
            </Typography>
            <Typography variant="subtitle2" mb={1}>
              Please sign up to our Inkfree portal to see all your signed
              documents.
            </Typography>
            <Stack spacing={2} direction="row" justifyContent="center">
              <Button onClick={handleLogin} variant="contained" size="small">
                Login
              </Button>
              <Button onClick={handleSignUp} variant="outlined" size="small">
                Sign Up
              </Button>
              <Button
                onClick={handleCloseSignerLogin}
                variant="outlined"
                size="small"
                color="inherit"
              >
                Close
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Slide>
  );

  const allFieldsFilledContent = (
    <Box textAlign="center">
      <Typography gutterBottom variant="body1" fontWeight="bold">
        You've filled in all the required fields.
      </Typography>
      <Typography
        gutterBottom
        variant="subtitle1"
        color="text.secondary"
        mb={2}
      >
        Are you ready to complete the signing process?
      </Typography>
      <Stack spacing={2} direction="row" justifyContent="center">
        <Button
          onClick={handleCancel}
          variant="outlined"
          size="medium"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={(event) => props.handleFinish(event)}
          variant="contained"
          size="medium"
          disabled={props.loading}
        >
          {props.loading ? "Processing..." : "Complete Signing"}
        </Button>
      </Stack>
    </Box>
  );

  const renderAllFieldsFilledPrompt = () => {
    // Mobile: the Modal System shell — which drops to a bottom sheet at this
    // width. The prompt keeps its own centred copy and inline actions.
    if (isMobile) {
      return (
        <AppModal
          open={props.allFieldsFilledPopup}
          onClose={handleCancel}
          ariaLabel="Complete signing"
          tone="success"
          size="sm"
          busy={props.loading}
        >
          {allFieldsFilledContent}
        </AppModal>
      );
    }

    // Desktop: keep the slide-up banner anchored to the bottom.
    return (
      <Slide
        direction="up"
        in={props.allFieldsFilledPopup}
        timeout={transitionDuration}
        mountOnEnter
        unmountOnExit
      >
        <Paper
          elevation={3}
          role="status"
          aria-live="polite"
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack
            direction="row"
            p={2.5}
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            {allFieldsFilledContent}
          </Stack>
        </Paper>
      </Slide>
    );
  };

  return (
    <>
      {renderSignupPrompt()}
      {renderAllFieldsFilledPrompt()}
    </>
  );
};

export default ParticipantsPrompts;
